import { jsx } from 'react/jsx-runtime';
import { forwardRef, useRef } from 'react';
import { Button as Button$1, SHAPE, SIZE, KIND } from 'baseui/button';
import { extend, colord } from 'colord';
import mixPlugin from 'colord/plugins/mix';
import 'baseui';
import '../../theme/olx-theme/base-theme.js';
import '../../theme/olx-theme/theme.js';
import { useResolvedStyle, mergeStyles } from '../../theme/utils.js';
import '../../theme/types.js';
import { getOverrides, mergeOverrides } from '../../overrides.js';
import '../../types/polymorphism.js';
import useMergedRef from '@react-hook/merged-ref';
import { tokens } from '../../theme/olx-theme/ds-tokens.js';
import { LoadingSpinner } from './loading-spinner.js';
import { StyledContentContainer } from './styled-components.js';
import style from './style.module.css.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
extend([mixPlugin]);
const BUTTON_KIND = __spreadProps(__spreadValues({}, KIND), {
  flat: "flat"
});
const getButtonSizeStyles = (size, $theme) => {
  switch (size) {
    case "small":
      return {
        fontSize: $theme.fontSizes[14],
        lineHeight: $theme.lineHeights[18],
        paddingLeft: $theme.space[24],
        paddingRight: $theme.space[24],
        paddingBottom: $theme.space[12],
        paddingTop: $theme.space[12]
      };
    case "large":
    default:
      return {
        fontSize: $theme.fontSizes[16],
        lineHeight: $theme.lineHeights[20],
        paddingLeft: $theme.space[32],
        paddingRight: $theme.space[32]
      };
  }
};
const getBaseButtonStyles = ({ styleProps, kind, size, background }) => {
  const { $theme, $isFocusVisible } = styleProps;
  const sizeStyles = getButtonSizeStyles(size, $theme);
  const inversed = background === "inverse";
  const commonStyles = __spreadValues(__spreadProps(__spreadValues({}, sizeStyles), {
    fontWeight: $theme.fontWeights.medium,
    boxShadow: "none",
    alignSelf: "center",
    transitionProperty: "color, background-color, box-shadow",
    transitionDuration: $theme.animation.duration[150],
    transitionTimingFunction: $theme.animation.timingFn.ease
  }), $isFocusVisible && $theme.focusOutline);
  switch (kind) {
    case BUTTON_KIND.secondary:
      return __spreadValues(__spreadProps(__spreadValues({}, commonStyles), {
        backgroundColor: "transparent",
        boxShadow: `inset 0 0 0 ${tokens.borderWidths.medium} ${$theme.colors["borders-brand-primary"]}`,
        ":active:not(:disabled)": {
          backgroundColor: "transparent",
          boxShadow: `inset 0 0 0 ${tokens.borderWidths.xLarge} ${$theme.colors["borders-brand-primary"]}`
        },
        ":hover:not(:active):not(:disabled)": {
          backgroundColor: "transparent",
          boxShadow: `inset 0 0 0 ${tokens.borderWidths.large} ${$theme.colors["borders-brand-primary"]}`
        },
        ":disabled": {
          backgroundColor: "transparent",
          boxShadow: `inset 0 0 0 ${tokens.borderWidths.medium} ${$theme.colors["borders-global-tertiary"]}`
        }
      }), inversed && {
        color: $theme.colors["text-global-inverse"],
        boxShadow: `inset 0 0 0 ${tokens.borderWidths.medium} ${$theme.colors["borders-global-inverse"]}`,
        ":active:not(:disabled)": {
          backgroundColor: "transparent",
          boxShadow: `inset 0 0 0 ${tokens.borderWidths.xLarge} ${$theme.colors["borders-global-inverse"]}`
        },
        ":hover:not(:active):not(:disabled)": {
          backgroundColor: "transparent",
          boxShadow: `inset 0 0 0 ${tokens.borderWidths.large} ${$theme.colors["borders-global-inverse"]}`
        }
      });
    case BUTTON_KIND.tertiary:
      return __spreadValues(__spreadProps(__spreadValues({}, commonStyles), {
        paddingLeft: $theme.space[2],
        paddingRight: $theme.space[2]
      }), inversed && {
        color: $theme.colors["text-global-inverse"]
      });
    case BUTTON_KIND.flat:
      return __spreadValues(__spreadProps(__spreadValues({}, commonStyles), {
        color: $theme.colors["text-brand-primary"],
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
        backgroundColor: "transparent",
        ":active": {
          backgroundColor: "transparent"
        },
        ":active:not(:disabled)": {
          color: colord($theme.colors["text-brand-primary"]).mix("#000", 0.24).toHex(),
          backgroundColor: "transparent"
        },
        ":hover": {
          backgroundColor: "transparent"
        },
        ":hover:not(:active):not(:disabled)": {
          color: colord($theme.colors["text-brand-primary"]).mix("#fff", 0.4).toHex(),
          backgroundColor: "transparent"
        },
        ":disabled": {
          backgroundColor: "transparent"
        }
      }), inversed && {
        color: $theme.colors["text-global-inverse"],
        ":active:not(:disabled)": {
          color: colord($theme.colors["text-global-inverse"]).mix("#000", 0.48).toHex(),
          backgroundColor: "transparent"
        },
        ":hover:not(:active):not(:disabled)": {
          color: colord($theme.colors["text-global-inverse"]).mix("#000", 0.64).toHex(),
          backgroundColor: "transparent"
        }
      });
    case BUTTON_KIND.primary:
    default:
      return __spreadValues(__spreadProps(__spreadValues({}, commonStyles), {
        position: "relative",
        ":hover:not(:active):not(:disabled)": {
          backgroundColor: colord($theme.colors["background-brand-inverse"]).mix("#fff", 0.4).toHex()
        },
        ":active:not(:disabled)": {
          backgroundColor: colord($theme.colors["background-brand-inverse"]).mix("#000", 0.24).toHex()
        }
      }), inversed && {
        color: $theme.colors["text-brand-primary"],
        backgroundColor: $theme.colors["background-global-primary"],
        ":hover:not(:active):not(:disabled)": {
          backgroundColor: colord($theme.colors["background-global-primary"]).mix("#000", 0.48).toHex()
        },
        ":active:not(:disabled)": {
          backgroundColor: colord($theme.colors["background-global-primary"]).mix("#000", 0.64).toHex()
        }
      });
  }
};
const Button = forwardRef((_a, forwardedRef) => {
  var _b = _a, { overrides, kind = BUTTON_KIND.primary, size = "large", background = "default", isLoading, $style, as, children } = _b, restProps = __objRest(_b, ["overrides", "kind", "size", "background", "isLoading", "$style", "as", "children"]);
  var _b2;
  const resolvedStyles = useResolvedStyle($style);
  const ref = useRef(null);
  const mergedRefs = useMergedRef(ref, forwardedRef);
  const _a2 = overrides != null ? overrides : {}, { ContentContainer: ContentContainerOverride } = _a2, restOverrides = __objRest(_a2, ["ContentContainer"]);
  const [ContentContainer, contentContainerProps] = getOverrides(ContentContainerOverride, StyledContentContainer);
  return jsx(Button$1, __spreadProps(__spreadValues(__spreadValues({ ref: mergedRefs, "$as": as, shape: SHAPE.default, size: SIZE.default, kind: (_b2 = KIND[kind]) != null ? _b2 : BUTTON_KIND.primary, isLoading, overrides: mergeOverrides({
    BaseButton: {
      style: (styleProps) => mergeStyles(getBaseButtonStyles({ styleProps, kind, size, background }), resolvedStyles),
      props: {
        className: kind === "tertiary" ? style.buttonTertiary : void 0
      }
    },
    LoadingSpinner: {
      component: LoadingSpinner,
      props: {
        size
      }
    },
    LoadingSpinnerContainer: {
      style: ({ $theme }) => ({
        marginTop: $theme.space[2],
        marginBottom: $theme.space[2]
      })
    },
    StartEnhancer: {
      style: ({ $theme }) => ({
        marginRight: $theme.space[8]
      })
    },
    EndEnhancer: {
      style: ({ $theme }) => ({
        marginLeft: $theme.space[8]
      })
    }
  }, restOverrides) }, !as && {
    type: "button",
    disabled: isLoading
  }), restProps), { children: jsx(ContentContainer, __spreadProps(__spreadValues({ "$kind": kind, className: kind === "tertiary" ? style.buttonTertiaryContentWrapper : void 0 }, contentContainerProps), { children })) }));
});

export { BUTTON_KIND, Button };
